import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { housingProductionData } from '../data/housingProductionData';
import './HousingProduction.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

const TYPES = [
  { value: 'Goal', label: 'Goals' },
  { value: 'Strategy', label: 'Strategies' },
];

function typeClass(kind) {
  if (kind === 'Goal') return 'hpp-type-goal';
  if (kind === 'Strategy') return 'hpp-type-strategy';
  return '';
}

function getAllItems(data) {
  const items = [];
  data.sections.forEach(s => s.items.forEach(it => items.push({ ...it, kind: s.kind })));
  return items;
}

function HousingProduction() {
  const [sel, setSel] = useState({ type: new Set() });
  const [expandedSections, setExpandedSections] = usePersistentSet('housing-production:expandedSections', () => new Set(housingProductionData.sections.map(s => s.id)));
  const [expandedItems, setExpandedItems] = usePersistentSet('housing-production:expandedItems');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('housing-production:overviewExpanded');
  const [keyExpanded, setKeyExpanded] = usePersistentFlag('housing-production:keyExpanded');

  useEffect(() => {
    document.title = 'Housing Production Plan';
  }, []);

  const allItems = useMemo(() => getAllItems(housingProductionData), []);

  const keyOf = (sectionId, idx) => `${sectionId}::${idx}`;

  const dimensions = useMemo(() => [
    { key: 'type', label: 'Type', values: TYPES.map(t => ({
      value: t.value, label: t.label,
      count: allItems.filter(i => i.kind === t.value).length,
    })) },
  ], [allItems]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ type: new Set() });

  const matchesSection = useMemo(() => {
    return (section) => !sel.type.size || sel.type.has(section.kind);
  }, [sel]);

  const filteredSections = useMemo(() => {
    return housingProductionData.sections.filter(matchesSection);
  }, [matchesSection]);

  const totalFiltered = useMemo(() => {
    return filteredSections.reduce((sum, s) => sum + s.items.length, 0);
  }, [filteredSections]);

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleItem = (k) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSections(new Set(housingProductionData.sections.map(s => s.id)));
    const allKeys = new Set();
    housingProductionData.sections.forEach(s => s.items.forEach((_, idx) => allKeys.add(keyOf(s.id, idx))));
    setExpandedItems(allKeys);
    setOverviewExpanded(true);
    setKeyExpanded(true);
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
    setExpandedItems(new Set());
    setOverviewExpanded(false);
    setKeyExpanded(false);
  };

  return (
    <div className="hpp-page">
      <a href="#main-content" className="hpp-skip-link">Skip to main content</a>
      <Header
        title="Housing Production Plan"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="hpp-content">
        <div className="hpp-intro">
          <p className="hpp-updated">
            Town of Brookline · {housingProductionData.planYear} Housing Production Plan · State-approved
          </p>
        </div>

        <div className="hpp-controls">
          <FilterBar
            allCount={allItems.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="hpp-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="hpp-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFiltered}
          noun="item"
          nounPlural="items"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="hpp-preamble-sections">
          <div className="hpp-preamble">
            <button
              className={`hpp-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`hpp-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="hpp-preamble-title">About This Plan</span>
            </button>
            {overviewExpanded && (
              <div className="hpp-preamble-body">
                {housingProductionData.introduction.overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="hpp-preamble">
            <button
              className={`hpp-preamble-header ${keyExpanded ? 'expanded' : ''}`}
              onClick={() => setKeyExpanded(!keyExpanded)}
              aria-expanded={keyExpanded}
            >
              <span className={`hpp-chevron${keyExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="hpp-preamble-title">How This Plan Is Organized</span>
            </button>
            {keyExpanded && (
              <div className="hpp-preamble-body">
                {housingProductionData.introduction.itemsNote.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
                <ul className="hpp-key-list">
                  {housingProductionData.legend.type.map(t => (
                    <li key={t.code}>
                      <span className={`hpp-type-badge ${typeClass(t.code)}`}>{t.code}</span>
                      <span className="hpp-key-label">{t.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="hpp-sections">
          {filteredSections.map(section => (
            <div key={section.id} className="hpp-section">
              <h2 className="hpp-heading"><button
                className={`hpp-section-header ${expandedSections.has(section.id) ? 'expanded' : ''}`}
                onClick={() => toggleSection(section.id)}
                aria-expanded={expandedSections.has(section.id)}
              >
                <span className={`hpp-chevron hpp-chevron-light${expandedSections.has(section.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                <div className="hpp-section-header-text">
                  <span className="hpp-section-name">{section.name}</span>
                  {section.description && (
                    <span className="hpp-section-desc">{section.description}</span>
                  )}
                </div>
                <span className="hpp-section-count" aria-hidden="true">{section.items.length}</span>
              </button></h2>

              {expandedSections.has(section.id) && (
                <div className="hpp-section-body">
                  {section.items.map((item, idx) => {
                    const k = keyOf(section.id, idx);
                    const isExpanded = expandedItems.has(k);
                    return (
                      <div key={k} className="hpp-item">
                        <h3 className="hpp-heading"><button
                          className={`hpp-item-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleItem(k)}
                          aria-expanded={isExpanded}
                        >
                          <span className={`hpp-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                          <span className="hpp-item-title">{item.title}</span>
                          <span className={`hpp-type-badge small ${typeClass(section.kind)}`}>{section.kind}</span>
                        </button></h3>
                        {isExpanded && (
                          <div className="hpp-item-body">
                            <p className="hpp-item-desc">{item.description}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="hpp-source">
          <p>
            Source: Brookline Housing Plan {housingProductionData.planYear} / Housing Production Plan (approved by
            the Commonwealth, November 12, 2024). This page presents the plan’s Housing Goals (Chapter 4) and
            Housing Strategies (Chapter 5); the housing needs assessment, challenges and opportunities analysis, and
            appendices are in the source document.
          </p>
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/1299/Housing-Production-Plan" target="_blank" rel="noopener noreferrer">
              Housing Production Plan<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default HousingProduction;
